import React, { useState, useEffect, useCallback } from "react";
import {
  Spinner,
  Alert,
  Button,
  Row,
  Col,
  Input,
  Badge,
} from "reactstrap";
import {
  getDocumentFormats,
  softDeleteDocumentFormat,
} from "../../ApiCalls/documentFormats";
import DocumentFormatModal from "./DocumentFormatModal";
import "./styles/documentFormatsTab.scss";

function DocumentFormatsTab({ mantisBusinessId, token, mantisBusinessUserId }) {
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [editingFormat, setEditingFormat] = useState(null);
  const [deletingFormatId, setDeletingFormatId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchFormats = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getDocumentFormats(token, mantisBusinessId, false);
      setFormats(Array.isArray(data.formats) ? data.formats : []);
    } catch (err) {
      setError(err.message || "Failed to load document formats");
    } finally {
      setLoading(false);
    }
  }, [token, mantisBusinessId]);

  useEffect(() => {
    fetchFormats();
  }, [fetchFormats]);

  const handleDeleteFormat = async (formatId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this document format? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setDeletingFormatId(formatId);
      setError("");
      await softDeleteDocumentFormat(token, formatId);
      setSuccess("Format deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
      await fetchFormats();
    } catch (err) {
      setError(err.message || "Failed to delete format");
    } finally {
      setDeletingFormatId(null);
    }
  };

  const handleEditFormat = (format) => {
    setEditingFormat(format);
    setShowFormatModal(true);
  };

  const handleCreateFormat = () => {
    setEditingFormat(null);
    setShowFormatModal(true);
  };

  const handleFormatSaved = () => {
    setShowFormatModal(false);
    setEditingFormat(null);
    setSuccess("Format saved successfully");
    setTimeout(() => setSuccess(""), 3000);
    fetchFormats();
  };

  const filteredFormats = formats.filter((format) =>
    format.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="document-formats-tab">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="document-formats-tab">
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      <div className="formats-header">
        <div className="header-left">
          <h5>📄 Document Format Templates</h5>
          <small className="text-muted">{filteredFormats.length} format(s)</small>
        </div>
        <Button color="primary" onClick={handleCreateFormat}>
          <i className="fas fa-plus" /> Create Format
        </Button>
      </div>

      {/* Search Input */}
      <Row className="mb-4">
        <Col xs={12}>
          <div className="search-wrapper">
            <i className="fas fa-search" />
            <Input
              type="text"
              placeholder="Search by format name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </Col>
      </Row>

      {filteredFormats.length === 0 ? (
        <Alert color="info">
          {searchTerm
            ? "No formats match your search"
            : "No document formats yet. Click 'Create Format' to add your first template."}
        </Alert>
      ) : (
        <div className="formats-grid">
          {filteredFormats.map((format) => (
            <div key={format._id} className="format-card">
              <div className="format-header">
                <h6 className="format-name">{format.name}</h6>
                {format.isDeleted && <Badge color="danger">Deleted</Badge>}
              </div>

              <div className="format-info">
                <div className="info-item">
                  <span className="label">Fields:</span>
                  <span className="value">{format.fields?.length || 0}</span>
                </div>
                <div className="info-item">
                  <span className="label">Created:</span>
                  <span className="value">
                    {format.dateCreated
                      ? new Date(format.dateCreated).toLocaleDateString()
                      : "—"}
                  </span>
                </div>
              </div>

              {format.suggestedFilename && (
                <div className="format-suggestion">
                  <i className="fas fa-file" />
                  <span>{format.suggestedFilename}</span>
                </div>
              )}

              <div className="fields-preview">
                <strong className="preview-title">Fields:</strong>
                {format.fields && format.fields.length > 0 ? (
                  <div className="fields-list">
                    {format.fields.slice(0, 3).map((field, idx) => (
                      <span key={idx} className="field-tag">
                        {field.name}
                      </span>
                    ))}
                    {format.fields.length > 3 && (
                      <span className="field-more">
                        +{format.fields.length - 3} more
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-muted">No fields defined</span>
                )}
              </div>

              <div className="format-actions">
                <Button
                  size="sm"
                  color="info"
                  onClick={() => handleEditFormat(format)}
                  disabled={format.isDeleted}
                >
                  <i className="fas fa-edit" /> Edit
                </Button>
                <Button
                  size="sm"
                  color="danger"
                  onClick={() => handleDeleteFormat(format._id)}
                  disabled={deletingFormatId === format._id || format.isDeleted}
                >
                  {deletingFormatId === format._id ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="fas fa-trash" /> Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Format Modal */}
      <DocumentFormatModal
        isOpen={showFormatModal}
        toggle={() => setShowFormatModal(false)}
        format={editingFormat}
        mantisBusinessId={mantisBusinessId}
        mantisBusinessUserId={mantisBusinessUserId}
        token={token}
        onSave={handleFormatSaved}
      />
    </div>
  );
}

export default DocumentFormatsTab;
